import * as Sentry from '@sentry/nextjs';

type LogAttributes = Record<string, unknown>;

/**
 * Server-only structured logger for SSR debugging. Sends every level to
 * Sentry's Logs product (queryable, never shipped to the client bundle);
 * `warn`/`error` also mirror to console for immediate local/Cloud Logging
 * visibility. Pass structured `attributes` (e.g. projectId, route) instead
 * of raw PII (email, phone, tokens) — Sentry's `beforeSendLog` only catches
 * obvious patterns, not a substitute for not logging PII in the first place.
 * Reserve `debug`/`info` for narrow diagnostics, not per-request happy-path
 * logging, to avoid burning through Sentry's log ingestion quota.
 */
export const ssrLogger = {
  debug: (message: string, attributes?: LogAttributes) => {
    Sentry.logger.debug(message, attributes);
  },
  info: (message: string, attributes?: LogAttributes) => {
    Sentry.logger.info(message, attributes);
  },
  warn: (message: string, attributes?: LogAttributes) => {
    Sentry.logger.warn(message, attributes);
    console.warn(message, attributes);
  },
  error: (message: string, attributes?: LogAttributes) => {
    Sentry.logger.error(message, attributes);
    console.error(message, attributes);
  },
};

/**
 * Shared reporter for the survey app's error.tsx/global-error.tsx boundaries:
 * logs via ssrLogger and forwards to Sentry. Extracted as a plain function
 * (rather than inlined in the boundary components) so it's unit-testable
 * without rendering — these boundaries are 'use client' and rely on hooks,
 * which this repo's test setup (no @testing-library/react) can't render.
 */
export function reportSurveyRenderError(
  error: Error & { digest?: string },
  attributes?: LogAttributes
) {
  ssrLogger.error('Survey error boundary caught a render error', {
    message: error.message,
    digest: error.digest,
    ...attributes,
  });
  Sentry.captureException(error);
}
